// ============================================================================
// QAWorkbench — v0.12 visual polish.
// Owner: Claude 3 (Frontend & UX).
// Purpose: QA approver landing screen — pending approvals + recently rejected
//          + training-impact warnings. Server enforces SoD (author != approver,
//          initiator != approver). The frontend just renders what the server
//          says is approvable.
// URS rows: WF-010, SIG-001, AUD-006, RBAC-004/005.
// ============================================================================
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  useQAPendingApprovals,
  useQARejectedRecent,
  useTrainingImpactWarnings,
} from '../../api/hooks';
import { AIAdvisoryBadge } from './components/AIAdvisoryBadge';
import { PageHero, StatCard, Stagger, Skeleton, SkeletonText } from '@edms/design-system';
import styles from './QAWorkbench.module.css';

function initialsOf(name?: string): string {
  if (!name) return '··';
  const parts = name.split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '··';
}

function isBreached(time: string): boolean {
  if (!time) return false;
  return /\d+d\b/.test(time);
}

export function QAWorkbench() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pending = useQAPendingApprovals();
  const rejected = useQARejectedRecent();
  const impact = useTrainingImpactWarnings();

  const counts = useMemo(() => {
    const items = pending.data ?? [];
    const breached = items.filter((w) => isBreached(w.timeInState)).length;
    const aiSuggested = items.filter((w) => w.hasAiSuggestion).length;
    return {
      pending: items.length,
      breached,
      aiSuggested,
      rejected7d: rejected.data?.length ?? 0,
    };
  }, [pending.data, rejected.data]);

  return (
    <main className={styles.page} aria-labelledby="qa-page-title">
      <PageHero
        eyebrow={t('qa.workbench_eyebrow', { defaultValue: 'Quality' })}
        title={t('qa.workbench_heading', { defaultValue: 'QA Workbench' })}
        subtitle={t('qa.workbench_subtitle', {
          defaultValue:
            'Documents waiting on QA approval, training-impact warnings, and signatures rejected in the last 7 days. Server enforces separation of duties; the AI advisory chip never auto-signs.',
        })}
      />

      <Stagger className={styles.kpiStrip} step={50} aria-label="QA workbench KPI tiles">
        {pending.isLoading ? (
          <Skeleton height={100} radius={14} />
        ) : (
          <StatCard
            label="Pending QA"
            value={counts.pending}
            deltaCaption={counts.breached > 0 ? `${counts.breached} past SLA` : 'all within SLA'}
            accent={counts.breached > 0 ? 'var(--color-status-error, #DC2626)' : counts.pending > 0 ? 'var(--color-status-warning, #D97706)' : 'var(--color-status-success, #16A34A)'}
          />
        )}
        {pending.isLoading ? (
          <Skeleton height={100} radius={14} />
        ) : (
          <StatCard
            label="AI flagged"
            value={counts.aiSuggested}
            deltaCaption="workflows with advisory"
            accent={counts.aiSuggested > 0 ? 'var(--color-status-warning, #D97706)' : 'var(--color-status-success, #16A34A)'}
          />
        )}
        {pending.isLoading ? (
          <Skeleton height={100} radius={14} />
        ) : (
          <StatCard
            label="Training impact"
            value={impact.data?.length ?? 0}
            deltaCaption="workflows trigger R&U"
            accent={(impact.data?.length ?? 0) > 0 ? 'var(--color-status-warning, #D97706)' : 'var(--color-status-success, #16A34A)'}
          />
        )}
        {pending.isLoading ? (
          <Skeleton height={100} radius={14} />
        ) : (
          <StatCard
            label="Rejected · 7 d"
            value={counts.rejected7d}
            deltaCaption="signature refusals"
            accent={counts.rejected7d > 0 ? 'var(--color-status-warning, #D97706)' : 'var(--color-status-success, #16A34A)'}
          />
        )}
      </Stagger>

      <section className={styles.panel} aria-labelledby="qa-pending">
        <header className={styles.panelHead}>
          <h2 id="qa-pending" className={styles.panelTitle}>
            {t('qa.pending_panel', { defaultValue: 'Pending my approval' })}
          </h2>
          <span className={styles.panelCount}>{counts.pending}</span>
        </header>
        {pending.isLoading && <SkeletonText lines={3} />}
        {pending.isError && (
          <p className={styles.empty} role="alert">
            {String(pending.error)}
          </p>
        )}
        {pending.data && pending.data.length === 0 && (
          <p className={styles.empty}>
            {t('qa.no_pending', { defaultValue: 'Nothing waiting on your signature.' })}
          </p>
        )}
        {pending.data && pending.data.length > 0 && (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('qa.col.document_number', { defaultValue: 'Document' })}</th>
                <th>{t('qa.col.author', { defaultValue: 'Author' })}</th>
                <th>{t('qa.col.initiator', { defaultValue: 'Initiator' })}</th>
                <th>{t('qa.col.time_in_state', { defaultValue: 'In stage' })}</th>
                <th>{t('qa.col.training_impact', { defaultValue: 'Training' })}</th>
                <th>{t('qa.col.actions', { defaultValue: 'Actions' })}</th>
              </tr>
            </thead>
            <tbody>
              {pending.data.map((w) => {
                const breach = isBreached(w.timeInState);
                return (
                  <tr key={w.workflowId}>
                    <td>
                      <div className={styles.docCell}>
                        <Link to="/workflows/$id" params={{ id: w.workflowId }} className={styles.docNumber}>
                          {w.documentNumber}
                        </Link>
                        <span className={styles.docTitle}>{w.title}</span>
                      </div>
                    </td>
                    <td>
                      <div className={styles.personCell}>
                        <span className={styles.avatar}>{initialsOf(w.authorName)}</span>
                        <span>{w.authorName}</span>
                      </div>
                    </td>
                    <td>
                      <div className={styles.personCell}>
                        <span className={styles.avatar}>{initialsOf(w.initiatorName)}</span>
                        <span>{w.initiatorName}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`${styles.timePill} ${breach ? styles.timePill_breach : ''}`}>
                        {w.timeInState}
                      </span>
                    </td>
                    <td>
                      {w.estimatedSignaturesRequired > 0 ? (
                        <span className={styles.trainingChip} title="Approving this version triggers R&U signatures">
                          {w.estimatedSignaturesRequired} R&amp;U
                        </span>
                      ) : (
                        <span style={{ color: 'var(--color-text-tertiary, #6B7989)' }}>—</span>
                      )}
                    </td>
                    <td>
                      <div className={styles.actions}>
                        {w.hasAiSuggestion && <AIAdvisoryBadge workflowId={w.workflowId} />}
                        {/* GMP: approval/rejection is an e-signature decision — both open the
                            review screen where the Part-11 signature ceremony is completed.
                            A one-click unsigned decision would be rejected by the backend. */}
                        <button
                          type="button"
                          className={`${styles.btn} ${styles.btn_primary}`}
                          onClick={() => navigate({ to: '/workflows/$id', params: { id: w.workflowId } })}
                        >
                          {t('qa.action.approve', { defaultValue: 'Approve' })}
                        </button>
                        <button
                          type="button"
                          className={`${styles.btn} ${styles.btn_reject}`}
                          onClick={() => navigate({ to: '/workflows/$id', params: { id: w.workflowId } })}
                        >
                          {t('qa.action.reject', { defaultValue: 'Reject' })}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <section className={styles.panel} aria-labelledby="qa-impact">
        <header className={styles.panelHead}>
          <h2 id="qa-impact" className={styles.panelTitle}>
            {t('qa.training_impact_panel', { defaultValue: 'Training-impact warnings' })}
          </h2>
          <span className={styles.panelCount}>{impact.data?.length ?? 0}</span>
        </header>
        {impact.isLoading && <SkeletonText lines={3} />}
        {impact.isError && (
          <p className={styles.empty} role="alert">
            {String(impact.error)}
          </p>
        )}
        {impact.data && impact.data.length === 0 && (
          <p className={styles.empty}>
            {t('qa.no_training_impact', {
              defaultValue: 'No workflows in the queue will trigger Read-and-Understood signatures on approval.',
            })}
          </p>
        )}
        {impact.data && impact.data.length > 0 && (
          <ul className={styles.warnList}>
            {impact.data.map((w) => (
              <li key={w.workflowId} className={styles.warnRow}>
                <span className={styles.warnIcon} aria-hidden>
                  ⚠️
                </span>
                <div className={styles.warnBody}>
                  <span className={styles.warnTitle}>{w.documentNumber}</span>
                  <span className={styles.warnSub}>
                    Approving this version will queue {w.estimatedSignaturesRequired} R&amp;U signatures across the training population.
                  </span>
                </div>
                <span className={styles.warnRight}>
                  {w.estimatedSignaturesRequired} {t('qa.signatures_required', { defaultValue: 'signatures' })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className={styles.panel} aria-labelledby="qa-rejected">
        <header className={styles.panelHead}>
          <h2 id="qa-rejected" className={styles.panelTitle}>
            {t('qa.rejected_panel', { defaultValue: 'Recently rejected · 7 days' })}
          </h2>
          <span className={styles.panelCount}>{rejected.data?.length ?? 0}</span>
        </header>
        {rejected.isLoading && <SkeletonText lines={3} />}
        {rejected.isError && (
          <p className={styles.empty} role="alert">
            {String(rejected.error)}
          </p>
        )}
        {rejected.data && rejected.data.length === 0 && (
          <p className={styles.empty}>
            {t('qa.no_rejected', { defaultValue: 'No signature rejections in the last 7 days.' })}
          </p>
        )}
        {rejected.data && rejected.data.length > 0 && (
          <ul className={styles.warnList}>
            {rejected.data.map((w) => (
              <li key={w.workflowId} className={`${styles.warnRow} ${styles.warnRow_neutral}`}>
                <span className={styles.warnIcon} aria-hidden>
                  ↩️
                </span>
                <div className={styles.warnBody}>
                  <span className={styles.warnTitle}>{w.documentNumber}</span>
                  <span className={styles.warnSub}>
                    {t('qa.rejected_on', { defaultValue: 'Rejected' })}{' '}
                    {new Date(w.rejectedAt).toLocaleString()}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}
